import { Color } from '@sapiens/insured-component-library';
import { ThemeMode, ThemeUnit } from '../helpers/theme';
import { DefaultTheme } from 'styled-components';

export enum MEDIA_WIDTH {
  MAX_WIDTH = 'max-width',
  MIN_WIDTH = 'min-width',
}

export const AT_MEDIA = '@media';

export const THEME_DEFAULTS = {
  MODE: 'light' as ThemeMode,
  UNIT: 'px' as ThemeUnit,
  // eslint-disable-next-line quotes
  FONT_FAMILY: "'Eina 03',  sans-serif",
  SPACING_FACTOR: 4,
  BREAKPOINTS: {
    xxs: 0,
    xs: 380,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
  },
  Z_INDEX: {
    header: 1000,
    calendar: 2,
    dropdown: 3,
  },
  // add non available colors here
  // A color once available shall be removed from this
  LKF_COLORS: {
    F60: '#F2FEF6',
    F999: '#28534E',
    F998: '#54B75A',
    F997: '#F8F8F8',
    F996: '#244b46',
    F994: '#E5E5E5',
    C100: '#13544e',
    C80: '#22968b',
    C70: '#29b7aa',
    C60: '#37d1c3',
    C50: '#59d9cd',
    C40: '#7AE0D7',
    C30: '#9be8e1',
    C20: '#bcefeb',
    C10: '#ddf7f5',
    G100: '#02ba4d',
    G90: '#02d95a',
    G80: '#02f967',
    G70: '#1ffc79',
    G60: '#3ffc8c',
    G50: '#5ffd9f',
    G40: '#7ffdb2',
    G30: '#9ffdc5',
    G20: '#bffed8',
    G10: '#dffeeb',
    N80: '#333333',
    N70: '#4c4c4c',
    N40: '#c1c1c1',
    N30: '#d6d6d6',
    N20: '#eaeaea',
    N10: '#f2f2f2',
    O100: '#ffab00',
    O90: '#ffb319',
    O80: '#ffbb32',
    O70: '#fec44c',
    O60: '#ffcc65',
    O50: '#ffd47f',
    O40: '#ffdd99',
    O30: '#ffe5b2',
    O20: '#feeecc',
    O10: '#fff6e5',
    R100: '#d22630',
    R90: '#da3740',
    R80: '#de4d55',
    R70: '#e2636b',
    R60: '#e67980',
    R50: '#ea9095',
    R40: '#eea6aa',
    R30: '#f2bcbf',
    R20: '#f6d2d4',
    R10: '#fae8e9',
    B100: '#0a398e',
    B90: '#0c46af',
    B80: '#0e53d0',
    B70: '#1462ee',
    B60: '#3678f0',
    B50: '#578ff3',
    B40: '#79a5f5',
    B30: '#9abbf7',
    B20: '#bcd2fa',
    B10: '#f4fbff',
    Y100: '#ffd600',
    Y90: '#ffda19',
    Y80: '#ffde32',
    Y70: '#fee24c',
    Y60: '#ffe665',
    Y50: '#ffea7f',
    Y40: '#ffee99',
    Y30: '#fff2b2',
    Y20: '#fef6cc',
    Y10: '#fffae5',
    A100: '#95deff',
    A90: '#9fe1ff',
    A80: '#aae4ff',
    A70: '#b4e7fe',
    A60: '#bfebff',
    A50: '#caeefe',
    A40: '#d4f1ff',
    A30: '#dff5fe',
    A20: '#e9f8ff',
    A10: '#f4fbff',
    P100: '#3c2867',
    P90: '#4b3281',
    P80: '#5a3c9b',
    P70: '#6a46b6',
    P60: '#7f60c1',
    P50: '#947acb',
    P40: '#a995d5',
    P30: '#bfafe0',
    P20: '#d4caea',
    P10: '#e9e4f4',
    U100: '#723300',
    U90: '#994400',
    U80: '#c15600',
    U70: '#e86800',
    U60: '#ff7b11',
    U50: '#ff9139',
    U40: '#ffa760',
    U30: '#ffbd88',
    U20: '#ffd3af',
    U10: '#ffe9d7',
    F400: '#007A84',
  },
};

export const THEME_OVERRIDES = (theme: DefaultTheme, spacing: (x: number) => string): any => ({
  badge: {
    color: {
      positive: {
        default: { color: '#000', backgroundColor: '#02BA4D' },
      },
      negative: {
        default: { color: '#FFF', backgroundColor: '#D22630' },
      },
    },
  },

  button: {
    root: {
      default: {
        fontFamily: 'Eina 03',
      },
    },
    primary: {
      root: {
        default: {
          backgroundColor: '#28534E',
          color: '#FFFFFF',
        },
        hover: {
          backgroundColor: '#32A0A9',
        },
        active: {
          backgroundColor: '#045B62',
          color: '#FFFFFF',
        },
      },
    },
    secondary: {
      root: {
        default: {
          backgroundColor: '#EBEEF0',
          color: '#00283B',
        },
        hover: {
          backgroundColor: '#DEE3E6',
        },
        active: {
          backgroundColor: '#00283B',
          color: '#FFFFFF',
        },
      },
    },
  },
  input: {
    adornment: {
      end: {
        svg: {
          default: { width: '12px !important' },
        },
      },
    },
    container: {
      root: {
        default: {
          display: 'flex',
          width: '100%',
          marginTop: '0px',
        },
      },
    },
    label: {
      default: {
        fontWeight: '600',
        fontSize: spacing(3),
        lineHeight: spacing(4),
        fontFamily: 'Eina 03',
      },
    },
    input: {
      root: {
        default: {
          flex: '1',
          backgroundColor: Color.white,
          marginRight: '0px !important',
          marginTop: '0px',
          fontFamily: 'Eina 03',
        },
      },
    },
  },
  dropdown: {
    root: {
      default: {
        fontFamily: 'Eina 03',
      },
    },
    icon: {
      root: {
        default: {
          marginRight: spacing(2),
        },
      },
    },
    option: {
      root: {
        hover: {
          backgroundColor: Color.white,
        },
        default: {
          fontFamily: 'Eina 03',
        },
      },
      content: {
        default: {
          marginLeft: '12px',
        },
      },
    },
    sizeStyling: {
      default: {
        selector: {
          default: {
            padding: `${spacing(2.5)} ${spacing(8)} ${spacing(2.5)} ${spacing(3)}`,
          },
        },
      },
    },
  },
  tabs: {
    tabLabelContainer: {
      root: {
        default: {
          borderBottom: '1px solid #1A756C',
        },
      },
    },
    tabLabel: {
      root: {
        default: {
          fontFamily: 'Eina 03',
          color: '#7F7F7F',
          fontWeight: 600,
        },
        hover: {
          color: '#00283B',
          fontWeight: 600,
        },
      },
      active: {
        root: {
          default: {
            fontFamily: 'Eina 03',
            color: '#13544E',
            fontWeight: 600,
          },
        },
      },
      after: {
        root: {
          default: {
            backgroundColor: '#13544E',
            fontWeight: 600,
          },
        },
      },
    },
    tabContentContainer: {
      root: {
        default: {
          color: '#00283B',
          fontFamily: 'Eina 03',
        },
      },
    },
  },

  radio: {
    styledLabel: {
      root: {
        default: {
          width: '60px',
          height: '30px',
          color: '#28534E',
          boxShadow: '0px 1px 1px rgba(0, 40, 59, 0.25), 0px 0px 1px rgba(0, 40, 59, 0.31)',
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: '24px',
        },
      },
    },
    styledRadio: {
      root: {
        default: {
          borderColor: '#28534E',
        },
      },
      checkedEnabled: {
        default: {
          backgroundColor: '#28534E',
          borderColor: '#28534E',
        },
      },
    },
  },

  radioGroup: {
    styledFieldset: {
      root: {
        default: {
          display: 'flex',
        },
      },
    },
  },
  fileUploader: {
    styledUploadButtonWrap: {
      button: {
        root: {
          default: {
            fontFamily: 'Eina 03',
          },
        },
      },
    },
    styledSingleFile: {
      root: {
        default: {
          background: '#F2FEF6',
        },
      },
    },
  },

  modal: {
    dialog: {
      content: {
        default: {
          width: '880px',
        },
      },
    },
    header: {
      root: {
        default: {
          fontFamily: 'Eina 03',
          fontWeight: 600,
        },
      },
    },
    footer: {
      root: {
        default: {
          justifyContent: 'center',
        },
      },
    },
  },
  checkbox: {
    styledLabel: {
      default: {
        width: '12px',
      },
    },
    styledCheckbox: {
      root: {
        default: {
          width: '12px',
        },
      },
      checkedOrIndeterminate: {
        default: {
          backgroundColor: '#244B46',
        },
      },
    },
  },
  iconPopover: {
    dialog: {
      content: {
        default: {},
      },
    },
    popover: {
      root: {
        default: {
          boxShadow: '0px 2px 6px rgba(69, 69, 69, 0.2)',
        },
      },
      anchor: {
        default: {
          fontFamily: 'Eina 03',
        },
      },
    },
  },
});
